import {
  type CustomerOrder,
  PRODUCT_TURNAROUNDS,
  type Project,
  type TitleProject,
  titleDraftOptionsSchema,
  type Workspace,
} from '@noconflict/contracts';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useOrderReferenceRequired, useProjectOrder, useQuote } from '../../entities/order';
import { useOrgContext } from '../../entities/org';
import { useTitleProject } from '../../entities/title-project';
import { useDeliveryEstimates } from '../../features/order/delivery-estimate';
import { DiscardDraftButton } from '../../features/order/discard-draft';
import {
  PriceSummary,
  type SummaryPrice,
  type SummaryRow,
} from '../../features/order/price-summary';
import { TitleOrderForm, useTitleDraft } from '../../features/order/title-order-form';
import { useFormat } from '../../shared/i18n/format';
import { ErrorState } from '../../shared/ui/error-state';
import { Skeleton } from '../../shared/ui/skeleton';
import { SubmitOrder } from '../../widgets/order-panel';
import { OrderDetails } from './order-details';

/**
 * A title clearance project. While it is a draft, and for someone who may order and edit here, it is the order
 * form with the price summary beside it. From submit on it is read-only: the details and choices, then the pay
 * controls and **Edit order** while it awaits payment, or the due date once it is placed.
 */
export function TitleProjectView({
  project,
  workspace,
}: {
  project: Project;
  workspace: Workspace;
}) {
  const { t } = useTranslation('order');
  const { org, canIn } = useOrgContext();
  const details = useTitleProject(project.id);
  const order = useProjectOrder(project.id);
  const referenceRequired = useOrderReferenceRequired(org.id);
  const archived = workspace.archived_at !== null;
  const canOrder = canIn(workspace.id, 'orders.create');
  const canEdit = canIn(workspace.id, 'projects.edit');

  if (details.isError || order.isError) {
    const failed = details.isError ? details : order;
    return (
      <ErrorState
        title={t('form.error')}
        description={failed.error?.message}
        onRetry={() => {
          void details.refetch();
          void order.refetch();
        }}
      />
    );
  }
  if (details.isPending || order.isPending || referenceRequired.isPending || !details.data) {
    return (
      <section aria-busy="true" aria-label={t('form.loading')} className="grid gap-4">
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-64 w-full" />
      </section>
    );
  }

  const o = order.data;
  const editable = o?.status === 'draft' && project.status === 'draft';
  if (editable && canOrder && canEdit && !archived) {
    return (
      <TitleDraftEditor
        project={project}
        workspace={workspace}
        details={details.data}
        order={o}
        referenceRequired={referenceRequired.data === true}
      />
    );
  }
  return (
    <TitleProjectDetails
      project={project}
      workspace={workspace}
      details={details.data}
      order={o}
      notice={editable ? (archived ? t('form.archived') : t('form.readOnly')) : null}
    />
  );
}

/** The editable draft: form, live price, delivery estimate, submit and discard. */
function TitleDraftEditor({
  project,
  workspace,
  details,
  order,
  referenceRequired,
}: {
  project: Project;
  workspace: Workspace;
  details: TitleProject;
  order: CustomerOrder;
  referenceRequired: boolean;
}) {
  const { t } = useTranslation('order');
  const navigate = useNavigate();
  const { org } = useOrgContext();
  const { formatCurrency } = useFormat();
  const draft = useTitleDraft({ projectId: project.id, details, order, referenceRequired });
  const estimates = useDeliveryEstimates(PRODUCT_TURNAROUNDS.title);
  const { scope, turnaroundDays, title } = draft.values;
  // No scope, no price: nothing is asked of the worker until both priced choices are made.
  const quote = useQuote(
    org.id,
    'title',
    { scope: scope ?? 'north_america', turnaroundDays },
    scope !== null,
  );
  const price: SummaryPrice =
    scope === null
      ? { state: 'incomplete' }
      : quote.isError
        ? { state: 'error' }
        : quote.data
          ? { state: 'ready', quote: quote.data }
          : { state: 'loading' };
  const amount =
    quote.data && 'amountCents' in quote.data && scope !== null
      ? formatCurrency(quote.data.amountCents, quote.data.currency)
      : null;
  const estimate = estimates.get(turnaroundDays);
  const rows: SummaryRow[] = [
    { label: t('review.report'), value: t('products.title'), wideOnly: true },
    { label: t('review.name'), value: title.trim() || t('drafts.untitled'), wideOnly: true },
    { label: t('review.scope'), value: scope ? t(`scope.${scope}`) : null },
    { label: t('review.turnaround'), value: t('turnaround', { count: turnaroundDays }) },
    ...(estimate
      ? [{ label: t('review.delivery'), value: estimate.deliveredBy, wideOnly: true }]
      : []),
  ];

  return (
    // The bottom padding keeps the last field clear of the summary bar on a phone.
    <div className="grid gap-6 pb-48 lg:grid-cols-[minmax(0,1fr)_20rem] lg:pb-0">
      <div className="grid gap-4">
        <TitleOrderForm draft={draft} estimates={estimates} referenceRequired={referenceRequired} />
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p
            role={draft.status === 'error' ? 'alert' : 'status'}
            className={
              draft.status === 'error'
                ? 'text-sm text-destructive'
                : 'text-sm text-muted-foreground'
            }
          >
            {draft.status === 'saving'
              ? t('form.saving')
              : draft.status === 'saved'
                ? t('form.saved')
                : draft.status === 'error'
                  ? `${t('form.saveError')} ${draft.error ?? ''}`
                  : null}
          </p>
          <DiscardDraftButton
            projectId={project.id}
            name={title.trim() || t('drafts.untitled')}
            label={t('form.discard')}
            onDiscarded={() => navigate(`/o/${org.slug}/w/${workspace.slug}`, { replace: true })}
          />
        </div>
      </div>
      <PriceSummary
        rows={rows}
        price={price}
        missing={draft.missing.map((field) => t(`review.missing.${field}`))}
        action={
          <SubmitOrder
            order={order}
            workspaceId={workspace.id}
            amount={amount}
            disabled={draft.missing.length > 0}
            before={draft.flush}
          />
        }
      />
    </div>
  );
}

/** From submit on, and for anyone who may not order here: the same details and choices, read-only, and the status. */
function TitleProjectDetails({
  project,
  workspace,
  details,
  order,
  notice,
}: {
  project: Project;
  workspace: Workspace;
  details: TitleProject;
  order: CustomerOrder | null;
  notice: string | null;
}) {
  const { t } = useTranslation('order');
  const options = titleDraftOptionsSchema.safeParse(order?.options).data;
  const rows: { label: string; value: string }[] = [
    { label: t('review.report'), value: t('products.title') },
    { label: t('review.name'), value: details.title ?? t('review.notEntered') },
    {
      label: t('review.productionType'),
      value: details.production_type
        ? t(`form.productionTypes.${details.production_type}`)
        : t('review.notChosen'),
    },
    ...(details.production_company
      ? [{ label: t('review.company'), value: details.production_company }]
      : []),
    ...(options
      ? [
          {
            label: t('review.scope'),
            value: options.scope ? t(`scope.${options.scope}`) : t('review.notChosen'),
          },
          {
            label: t('review.turnaround'),
            value: t('turnaround', { count: options.turnaroundDays }),
          },
        ]
      : []),
    ...(order?.reference ? [{ label: t('review.reference'), value: order.reference }] : []),
    ...(details.notes ? [{ label: t('review.notes'), value: details.notes }] : []),
  ];

  return (
    <OrderDetails
      project={project}
      workspace={workspace}
      order={order}
      notice={notice}
      rows={rows}
      wideLabels={[t('review.notes')]}
    />
  );
}
